import { UserProgress, UserProfile, Achievement, DifficultyLevel, VocabularyWord } from '../types';

const STORAGE_KEYS = {
  USER_PROGRESS: 'word-adventure-progress',
  USER_PROFILE: 'word-adventure-profile',
  CUSTOM_VOCABULARY: 'word-adventure-custom-vocab',
} as const;

class StorageService {
  /**
   * Get user progress from localStorage
   */
  getUserProgress(): UserProgress | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.USER_PROGRESS);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error reading user progress:', error);
      return null;
    }
  }

  /**
   * Save user progress to localStorage
   */
  saveUserProgress(progress: UserProgress): void {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_PROGRESS, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving user progress:', error);
    }
  }

  /**
   * Initialize default user progress
   */
  initializeProgress(userId: string): UserProgress {
    const today = new Date().toISOString().split('T')[0];
    const defaultProgress: UserProgress = {
      userId,
      totalScore: 0,
      gamesPlayed: 0,
      wordsLearned: [],
      levelProgress: {
        1: { unlocked: true, completionPercentage: 0, stars: 0 },
        2: { unlocked: false, completionPercentage: 0, stars: 0 },
        3: { unlocked: false, completionPercentage: 0, stars: 0 },
        4: { unlocked: false, completionPercentage: 0, stars: 0 },
        5: { unlocked: false, completionPercentage: 0, stars: 0 },
      },
      achievements: [],
      lastPlayed: Date.now(),
      currentStreak: 0,
      longestStreak: 0,
      lastStreakDate: today,
      dailyGoal: 10,
      dailyProgress: 0,
      lastDailyReset: today,
      weeklyGames: [0, 0, 0, 0, 0, 0, 0],
      weeklyScores: [0, 0, 0, 0, 0, 0, 0],
    };

    this.saveUserProgress(defaultProgress);
    return defaultProgress;
  }

  /**
   * Update user progress with new data
   */
  updateProgress(updates: Partial<UserProgress>): UserProgress {
    const current = this.getUserProgress();
    const updated: UserProgress = {
      ...(current || this.initializeProgress('default-user')),
      ...updates,
      lastPlayed: Date.now(),
    };

    this.saveUserProgress(updated);
    return updated;
  }

  /**
   * Add score to user's total
   */
  addScore(points: number): UserProgress {
    const current = this.getUserProgress() || this.initializeProgress('default-user');
    return this.updateProgress({
      totalScore: current.totalScore + points,
    });
  }

  /**
   * Mark a word as learned
   */
  markWordLearned(wordId: string): UserProgress {
    const current = this.getUserProgress() || this.initializeProgress('default-user');
    if (!current.wordsLearned.includes(wordId)) {
      return this.updateProgress({
        wordsLearned: [...current.wordsLearned, wordId],
      });
    }
    return current;
  }

  /**
   * Update level progress
   */
  updateLevelProgress(
    level: DifficultyLevel,
    updates: { completionPercentage?: number; stars?: number; unlocked?: boolean }
  ): UserProgress {
    const current = this.getUserProgress() || this.initializeProgress('default-user');
    const updatedLevelProgress = {
      ...current.levelProgress,
      [level]: {
        ...current.levelProgress[level],
        ...updates,
      },
    };

    // Unlock next level if current level reaches 100%
    if (updates.completionPercentage === 100 && level < 5) {
      updatedLevelProgress[level + 1 as DifficultyLevel] = {
        ...updatedLevelProgress[level + 1 as DifficultyLevel],
        unlocked: true,
      };
    }

    return this.updateProgress({
      levelProgress: updatedLevelProgress,
    });
  }

  /**
   * Add an achievement
   */
  addAchievement(achievement: Achievement): UserProgress {
    const current = this.getUserProgress() || this.initializeProgress('default-user');
    const exists = current.achievements.some((a) => a.id === achievement.id);

    if (!exists) {
      return this.updateProgress({
        achievements: [
          ...current.achievements,
          { ...achievement, unlockedAt: Date.now() },
        ],
      });
    }

    return current;
  }

  /**
   * Increment games played counter
   */
  incrementGamesPlayed(): UserProgress {
    const current = this.getUserProgress() || this.initializeProgress('default-user');
    return this.updateProgress({
      gamesPlayed: current.gamesPlayed + 1,
    });
  }

  // User Profile methods
  /**
   * Get user profile from localStorage
   */
  getUserProfile(): UserProfile | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error reading user profile:', error);
      return null;
    }
  }

  /**
   * Save user profile to localStorage
   */
  saveUserProfile(profile: UserProfile): void {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(profile));
    } catch (error) {
      console.error('Error saving user profile:', error);
    }
  }

  /**
   * Create a new user profile
   */
  createProfile(name: string, avatar: string, preferredLanguage: any): UserProfile {
    const profile: UserProfile = {
      name,
      avatar,
      preferredLanguage,
      currentLevel: 1,
      createdAt: Date.now(),
    };

    this.saveUserProfile(profile);
    return profile;
  }

  /**
   * Update user profile
   */
  updateProfile(updates: Partial<UserProfile>): UserProfile {
    const current = this.getUserProfile();
    if (!current) {
      throw new Error('No profile found. Create a profile first.');
    }

    const updated: UserProfile = {
      ...current,
      ...updates,
    };

    this.saveUserProfile(updated);
    return updated;
  }

  /**
   * Update streak based on play activity
   */
  updateStreak(): UserProgress {
    const current = this.getUserProgress() || this.initializeProgress('default-user');
    const today = new Date().toISOString().split('T')[0];
    const lastDate = current.lastStreakDate;

    // Check if already played today
    if (lastDate === today) {
      return current;
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    let newStreak = current.currentStreak;

    if (lastDate === yesterdayStr) {
      // Played yesterday, continue streak
      newStreak = current.currentStreak + 1;
    } else if (lastDate === today) {
      // Already played today
      newStreak = current.currentStreak;
    } else {
      // Streak broken, start new
      newStreak = 1;
    }

    const longestStreak = Math.max(current.longestStreak, newStreak);

    return this.updateProgress({
      currentStreak: newStreak,
      longestStreak,
      lastStreakDate: today,
    });
  }

  /**
   * Reset daily progress if needed
   */
  checkDailyReset(): UserProgress {
    const current = this.getUserProgress() || this.initializeProgress('default-user');
    const today = new Date().toISOString().split('T')[0];

    if (current.lastDailyReset !== today) {
      return this.updateProgress({
        dailyProgress: 0,
        lastDailyReset: today,
      });
    }

    return current;
  }

  /**
   * Increment daily progress
   */
  incrementDailyProgress(): UserProgress {
    const current = this.checkDailyReset();
    return this.updateProgress({
      dailyProgress: current.dailyProgress + 1,
    });
  }

  /**
   * Update daily goal
   */
  setDailyGoal(goal: number): UserProgress {
    return this.updateProgress({
      dailyGoal: goal,
    });
  }

  /**
   * Update weekly stats
   */
  updateWeeklyStats(gamesPlayed: number, score: number): UserProgress {
    const current = this.getUserProgress() || this.initializeProgress('default-user');
    const dayOfWeek = new Date().getDay(); // 0 = Sunday, 6 = Saturday

    const newWeeklyGames = [...current.weeklyGames];
    const newWeeklyScores = [...current.weeklyScores];

    newWeeklyGames[dayOfWeek] += gamesPlayed;
    newWeeklyScores[dayOfWeek] += score;

    return this.updateProgress({
      weeklyGames: newWeeklyGames,
      weeklyScores: newWeeklyScores,
    });
  }

  /**
   * Get custom vocabulary from localStorage
   */
  getCustomVocabulary(): VocabularyWord[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CUSTOM_VOCABULARY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading custom vocabulary:', error);
      return [];
    }
  }

  /**
   * Save custom vocabulary to localStorage
   */
  saveCustomVocabulary(words: VocabularyWord[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.CUSTOM_VOCABULARY, JSON.stringify(words));
    } catch (error) {
      console.error('Error saving custom vocabulary:', error);
    }
  }

  /**
   * Add words to custom vocabulary
   */
  addCustomWords(newWords: VocabularyWord[]): VocabularyWord[] {
    const current = this.getCustomVocabulary();
    const updated = [...current, ...newWords];
    this.saveCustomVocabulary(updated);
    return updated;
  }

  /**
   * Delete a custom word by ID
   */
  deleteCustomWord(wordId: string): VocabularyWord[] {
    const current = this.getCustomVocabulary();
    const updated = current.filter((w) => w.id !== wordId);
    this.saveCustomVocabulary(updated);
    return updated;
  }

  /**
   * Clear all data (for testing or reset)
   */
  clearAllData(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.USER_PROGRESS);
      localStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
      localStorage.removeItem(STORAGE_KEYS.CUSTOM_VOCABULARY);
    } catch (error) {
      console.error('Error clearing data:', error);
    }
  }
}

// Export a singleton instance
export const storageService = new StorageService();
